import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ExternalLink, SearchInput } from "@octopusdeploy/design-system-components";
import type { WorkerPoolsSummaryResource, WorkerPoolResource, Repository } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useEffect } from "react";
import ConfirmTentacleUpgradePanel from "~/areas/infrastructure/components/MachinesLayout/ConfirmTentacleUpgradePanel";
import { useOnClearMachineDispatch } from "~/areas/infrastructure/hooks/useOnClearMachineDispatch";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout/index";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import { EndpointCommunicationStyleMultiSelect } from "~/components/MultiSelect/EndpointCommunicationStyleMultiSelect";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import { ShellNameMultiSelect } from "~/components/MultiSelect/ShellNameMultiSelect";
import { WorkerPoolMultiSelect } from "~/components/MultiSelect/WorkerPoolMultiSelect";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper/index";
import type { WorkerPoolsSummaryFilter } from "../WorkerPools/WorkerPoolsLayout/WorkerPoolsSummaryFilter";
import { createWorkerPoolsSummaryArgs, defaultWorkerPoolsSummaryFilter } from "../WorkerPools/WorkerPoolsLayout/WorkerPoolsSummaryFilter";
import WorkerMachinesSummarySection from "./WorkerMachinesSummarySection";
export type WorkersPageQueryParams = WorkerPoolsSummaryFilter;
interface WorkersPageProps {
    spaceId: string;
    loaderData: WorkersPageLoaderData;
    queryParams: WorkersPageQueryParams;
    setQueryParams: QueryParamValuesSetter<WorkersPageQueryParams>;
}
interface WorkersPageLoaderData {
    workerShellNames: string[];
    workerPools: WorkerPoolResource[];
    hasMachines: boolean;
    workerPoolsSummary: WorkerPoolsSummaryResource;
}
export const workersPageTitle = "Workers";
export async function workersPageLoader(repository: Repository, queryParams: WorkersPageQueryParams): Promise<WorkersPageLoaderData> {
    const defaultArgs = createWorkerPoolsSummaryArgs(queryParams);
    const workerPools = repository.WorkerPools.all();
    const workerShellNames = repository.WorkerShells.all();
    const workerPoolsSummary = repository.WorkerPools.summary(defaultArgs);
    const hasMachines = (await repository.Workers.list({ take: 0 })).TotalResults > 0;
    return {
        workerPools: await workerPools,
        workerShellNames: await workerShellNames,
        workerPoolsSummary: await workerPoolsSummary,
        hasMachines,
    };
}
export function WorkersPage({ spaceId, loaderData, queryParams, setQueryParams }: WorkersPageProps) {
    const machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    const communicationStyles = EndpointsHelper.getCommunicationStyleResources();
    const onClearMachine = useOnClearMachineDispatch();
    const showV2Page = useOctopusFeatureToggle("deployment-targets-page-uplift", false);
    const navigate = useSpaceAwareNavigation();
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.machinePartialName, (value) => setQueryParams((prev) => ({ ...prev, machinePartialName: value })), 200);
    useEffect(() => {
        onClearMachine();
    }, [onClearMachine]);
    const addWorkerPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Add Worker",
        path: links.selectWorkerTypePage.generateUrl({ spaceId }),
        hasPermissions: isAllowed({
            permission: Permission.WorkerEdit,
        }),
    };
    if (!loaderData.hasMachines) {
        return (<PortalEmptyStateOnboarding title={"Configure workers to execute deployment tasks on behalf of the Octopus Server"} description={"Workers let you delegate work to a machine other than the Octopus server for increased speed, scale and security."} primaryAction={addWorkerPageAction} learnMore={<ExternalLink href="OnboardingWorkersLearnMore">Learn more</ExternalLink>}/>);
    }
    const tentacleUpgradesRequiredWarning = loaderData.workerPoolsSummary.TentacleUpgradesRequired && <ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => navigate.navigate(links.tasksPage.generateUrl())}/>;
    const advancedFilters = (<AdvancedFilters>
            <AdvancedFilterCheckbox key="filterDisabled" label="Disabled only" value={queryParams.isDisabled ?? false} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
            <WorkerPoolMultiSelect accessibleName="workerPoolFilter" key="workerPoolIdsMultiSelect" items={loaderData.workerPools} value={queryParams.workerPoolIds} onChange={(x) => setQueryParams((prev) => ({ ...prev, workerPoolIds: x }))}/>
            <ShellNameMultiSelect accessibleName="shellNameFilter" key="filterShellName" items={loaderData.workerShellNames} value={queryParams.shellNames} onChange={(x) => setQueryParams((prev) => ({ ...prev, shellNames: x }))}/>
            <EndpointCommunicationStyleMultiSelect accessibleName="communicationStyleFilter" key="filterCommunication" items={communicationStyles.filter((cs) => cs.Name !== "Step Package")} value={queryParams.commStyles} onChange={(x) => setQueryParams((prev) => ({ ...prev, commStyles: x }))}/>
            <MachineModelHealthStatusMultiSelect accessibleName="healthStatusFilter" key="filterStatus" items={machineHealthStatuses} value={queryParams.healthStatuses} onChange={(x) => setQueryParams((prev) => ({ ...prev, healthStatuses: x }))}/>
        </AdvancedFilters>);
    const filters = {
        inputs: [<SearchInput placeholder={"Search by name"} accessibleName={"Search by worker name"} value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} autoFocus={true}/>],
        advancedFilters: {
            content: advancedFilters,
            hasUserSelectedValues: isFiltering(queryParams),
            onResetFilter: () => setQueryParams(defaultWorkerPoolsSummaryFilter),
        },
    };
    return (<PageContent header={{
            title: workersPageTitle,
            primaryAction: addWorkerPageAction,
        }} filters={filters} legacyStatus={status} callout={{ type: "custom", content: <>{tentacleUpgradesRequiredWarning}</> }}>
            <BorderedContent>
                <WorkerMachinesSummarySection key="allMachines" workerPoolsSummary={loaderData.workerPoolsSummary} filter={queryParams} workerPools={loaderData.workerPools} showV2Page={showV2Page}/>
            </BorderedContent>
        </PageContent>);
}
function isFiltering(currentFilter: WorkerPoolsSummaryFilter) {
    return !isEqual(currentFilter, defaultWorkerPoolsSummaryFilter);
}
